/**
 * Evaluating the money terms — the second aggregate's arithmetic.
 *
 * The types, the currency, and the argument for money being a sum rather than a
 * projection of hours all live in `coverage-money.types.ts`. This file is the
 * part that runs, and it is deliberately shaped so `coverage-ratio.ts` can call
 * it FROM INSIDE ITS EXISTING LOOP.
 *
 * ── WHY ONE TRAVERSAL AND NOT TWO PASSES ────────────────────────────────────
 *
 * A second pass over the same impacts would be simpler to write and would drift
 * on the first day. Three things have to stay in step per row, and a second
 * traversal is a join that can lose any of them:
 *
 * 1. THE CONTRIBUTION LIST. One row carries its hours AND its two money terms.
 *    Two passes produce two lists to merge by id, and a merge that misses an id
 *    silently drops a system from one aggregate and not the other.
 * 2. THE KEY SETS. `measuredInputKeys` is the union across EVERY model
 *    evaluated, money models included; `consumedInputKeys` is its denominator
 *    and is accumulated in the same branches that accumulate the AMOUNTS. A
 *    second pass has its own sets, and forgetting to union either one moves the
 *    published `basis` — a row whose cost is measured and whose hours are
 *    estimated is MIXED, and dropping the money side would call it ESTIMATED.
 * 3. THE SORT. `computeRoleCoverage` sorts by id before summing so a query plan
 *    cannot change the last decimal. A second pass would have to re-impose the
 *    identical order, and "identical" is a claim nobody would re-check.
 *
 * ── EVERY CHECK THE HOURS SIDE MAKES, MONEY MAKES TOO ───────────────────────
 *
 * Dimension, then admissibility, then agreement — the same three, in the same
 * order, for the same reasons, against `MONEY` instead of `TIME·PERSON`. The
 * order matters for the same reason it does on the hours side: a term must be
 * an amount of money in its OWN right before it is allowed to SET the basis
 * every other term is measured against, or one term annualised twice would set
 * `per year^2`, agree with itself, and be summed as money.
 *
 * ⚠️ **`€400 per MONTH` against `€4,000 per YEAR` is NOT the disagreement this
 * check catches, and reading it as one builds a fixture that proves nothing.**
 * `perPeriod` annualises EVERY period at input binding, so a monthly figure and
 * a yearly figure both arrive `ANNUAL` and agree. There are exactly two bases —
 * `ABSOLUTE` (0) and `ANNUAL` (-1) — so the only pair that can disagree is a
 * model that DECLARES a period beside one that declares NONE. That is also the
 * mistake authors actually make, and `coverage-period.types.ts` says so under
 * "where this axis does not reach": it catches a model that declares its period
 * on one side and forgets it on the other, and it cannot catch one that
 * declares it nowhere.
 *
 * Measured 2026-08-08: a per-month/per-year fixture stays green with this
 * file's entire disagreement branch deleted.
 *
 * ── THE ROLE ITSELF CARRIES TWO MONEY MODELS, NOT ONE ───────────────────────
 *
 * `RoleWorkload` holds the role's own cost AND the revenue the role holds that
 * is attached to no system. The second exists because a system-shaped model
 * cannot express revenue no system produces — a retention call a person holds —
 * and that revenue is real revenue of the role. Both go through
 * {@link evaluateWorkloadMoneyTerm}, which is one function for the same reason
 * {@link evaluateImpactMoneyTerm} is: revenue and cost fail identically.
 *
 * ── THERE IS NO MONEY RATIO, ON PURPOSE ─────────────────────────────────────
 *
 * `revenue ÷ role cost` is computable and would be a fabricated KPI. The hours
 * ratio means something precise — a share of the work this role performs — and
 * its denominator is the whole of that work. A role's annual cost is not the
 * whole of any money quantity the impacts belong to: revenue is not a share of
 * salaries. So the money side reports AMOUNTS, and the one derived figure it
 * does report (`net`) is a subtraction between two things in the same currency
 * over the same window, which is the only combination that is defensible.
 */
import type { ValidatedCoverageContext } from "./coverage-context.schema";
import { coverageDimensionsEqual, formatCoverageDimension } from "./coverage-dimension.types";
import { type CoverageQuantity, evaluateCoverageFormula } from "./coverage-evaluator";
import type { ValidatedCoverageFormula } from "./coverage-formula.schema";
import type { CoverageWarning } from "./coverage-integrity.types";
import {
  COVERAGE_MONEY_DIMENSION,
  type CoverageMoneyTerm,
  type CoverageMoneyTermName
} from "./coverage-money.types";
import {
  formatCoveragePeriodBasis,
  isAdmissibleCoverageRatioPeriodBasis
} from "./coverage-period.types";

/**
 * Does a money term's window disagree with the one this Role has already fixed?
 *
 * ONE DEFINITION, THREE CALLERS, AND THE THIRD IS WHY IT IS EXPORTED. The two
 * evaluators below each carried this expression inline and identically, which
 * was already one copy too many. The third caller is the WRITE boundary
 * (`role-coverage-authoring-input.schema.ts`), which refuses the disagreeing
 * pair before it is stored rather than reporting it on the next read — and a
 * write that refuses a different set from the one the read declines is worse
 * than no write check at all, because the two would each look right in
 * isolation.
 *
 * `requiredBasis === null` means no money term has fixed a window yet, and the
 * FIRST term through may hold any admissible basis. `!==` on a plain number is
 * the whole rule: there are exactly two admissible bases and neither is `NaN`.
 *
 * 🚨 **THE RETURN TYPE IS `requiredBasis is number`, NOT `boolean`, AND THAT IS
 * LOAD-BEARING RATHER THAN DECORATION.** The two branches below each render
 * `formatCoveragePeriodBasis(requiredBasis)` into the warning they raise, and
 * they were only able to because the inline `requiredBasis !== null` narrowed
 * the variable for the rest of the block. Extracting the test into an ordinary
 * `boolean` function DESTROYS that narrowing — `tsc` reported both call sites
 * the moment this function was first written as one. A type predicate hands the
 * narrowing back, so the fact that a disagreement implies a stated window is
 * carried by the signature instead of by a comment, and a caller cannot format
 * a `null` basis into a sentence about a window.
 */
export function coverageMoneyBasisDisagrees(
  basis: number,
  requiredBasis: number | null
): requiredBasis is number {
  return requiredBasis !== null && basis !== requiredBasis;
}

/**
 * What evaluating one money term produced.
 *
 * `periodBasis` is non-null exactly when `term.kind === "amount"` — a term that
 * did not survive every check may not set the basis the others are held to.
 */
export interface CoverageMoneyTermOutcome {
  readonly term: CoverageMoneyTerm;
  /** Empty when the term was absent or evaluated cleanly. */
  readonly warnings: readonly CoverageWarning[];
  /**
   * Every input key this term's evaluation bound — the denominator of the
   * ESTIMATED / MIXED / MEASURED derivation for whatever consumes the amount.
   *
   * Reported alongside {@link measuredInputKeys} on every arm, including a
   * rejected one, because both describe THIS evaluation. Deciding whether a
   * rejected term counts toward a basis is the CALLER's, and
   * `computeRoleCoverage` accumulates only inside the `kind === "amount"`
   * branch — see the basis note there.
   */
  readonly consumedInputKeys: readonly string[];
  readonly measuredInputKeys: readonly string[];
  readonly periodBasis: number | null;
}

/** A money term that was never authored: nothing to evaluate, nothing to warn about. */
const ABSENT_OUTCOME: CoverageMoneyTermOutcome = {
  term: { kind: "absent" },
  warnings: [],
  consumedInputKeys: [],
  measuredInputKeys: [],
  periodBasis: null
};

/**
 * Evaluate one system's revenue or cost model.
 *
 * `requiredBasis` is the window every money term of this role must agree on, or
 * `null` while none has been fixed yet. This function never mutates it — the
 * caller adopts `periodBasis` when it is non-null, which keeps "who decides the
 * basis" in one place instead of two.
 */
export function evaluateImpactMoneyTerm(
  id: string,
  termName: CoverageMoneyTermName,
  formula: ValidatedCoverageFormula | undefined,
  context: ValidatedCoverageContext,
  requiredBasis: number | null
): CoverageMoneyTermOutcome {
  if (formula === undefined) {
    return ABSENT_OUTCOME;
  }

  const subject = { kind: "impactMoney", id, term: termName } as const;
  const evaluated = evaluateCoverageFormula(formula, context);

  if (!evaluated.ok) {
    return {
      term: { kind: "failed", failure: evaluated.error },
      warnings: [
        {
          code: "IMPACT_MONEY_MODEL_INVALID",
          severity: "error",
          subject,
          message:
            `${termName} model of impact "${id}" did not evaluate ` +
            `(${evaluated.error.code}): ${evaluated.error.message}`
        }
      ],
      consumedInputKeys: [],
      measuredInputKeys: [],
      periodBasis: null
    };
  }

  const { quantity, consumedInputKeys, measuredInputKeys } = evaluated.value;

  // Both key sets are reported even when the term is rejected below, because
  // both are facts about THIS evaluation: these keys were bound, and these of
  // them came from a measurement. What a REFUSED term means for the
  // ESTIMATED / MIXED / MEASURED badge is not decided here — `coverage-ratio.ts`
  // accumulates the basis only where it accumulates the amount, so a term that
  // reaches no published figure reaches no basis either.
  const rejected = (warning: CoverageWarning): CoverageMoneyTermOutcome => ({
    term: { kind: "failed", failure: null },
    warnings: [warning],
    consumedInputKeys,
    measuredInputKeys,
    periodBasis: null
  });

  if (!isMoney(quantity)) {
    return rejected({
      code: "IMPACT_MONEY_WRONG_DIMENSION",
      severity: "error",
      subject,
      message:
        `${termName} model of impact "${id}" evaluates to ` +
        `${formatCoverageDimension(quantity.dimension)}, not ` +
        `${formatCoverageDimension(COVERAGE_MONEY_DIMENSION)} — it cannot be counted as ${termName}`
    });
  }

  if (!isAdmissibleCoverageRatioPeriodBasis(quantity.periodBasis)) {
    return rejected({
      code: "IMPACT_MONEY_INADMISSIBLE_PERIOD_BASIS",
      severity: "error",
      subject,
      message:
        `${termName} model of impact "${id}" is ` +
        `${formatCoveragePeriodBasis(quantity.periodBasis)} — money must be an absolute amount ` +
        "or an amount per year, so it cannot be counted"
    });
  }

  if (coverageMoneyBasisDisagrees(quantity.periodBasis, requiredBasis)) {
    return rejected({
      code: "IMPACT_MONEY_WRONG_PERIOD_BASIS",
      severity: "error",
      subject,
      message:
        `${termName} model of impact "${id}" is ` +
        `${formatCoveragePeriodBasis(quantity.periodBasis)} but this role's money figures are ` +
        `${formatCoveragePeriodBasis(requiredBasis)} — same dimension, different window, so it ` +
        "cannot be added to them"
    });
  }

  return {
    term: { kind: "amount", amount: quantity.magnitude },
    warnings: [],
    consumedInputKeys,
    measuredInputKeys,
    periodBasis: quantity.periodBasis
  };
}

/** What evaluating one of the role's own money models produced. */
export interface CoverageWorkloadMoneyOutcome {
  /** The amount, or `null` when absent or refused. */
  readonly amount: number | null;
  readonly warnings: readonly CoverageWarning[];
  /** Every input key this model's evaluation bound. See the impact twin. */
  readonly consumedInputKeys: readonly string[];
  readonly measuredInputKeys: readonly string[];
  /** Non-null exactly when `amount` is: only an admissible amount fixes the basis. */
  readonly periodBasis: number | null;
}

/** This model of the role's own was never authored. A normal state, not a warning. */
const ABSENT_WORKLOAD_MONEY: CoverageWorkloadMoneyOutcome = {
  amount: null,
  warnings: [],
  consumedInputKeys: [],
  measuredInputKeys: [],
  periodBasis: null
};

/** How each of the role's own money models refers to itself in a warning. */
const WORKLOAD_MONEY_SUBJECT_TEXT: Readonly<Record<CoverageMoneyTermName, string>> = {
  cost: "the role's own cost model",
  revenue: "the role's own revenue model"
};

/**
 * Evaluate one of the role's OWN money models — its annual cost (salaries,
 * seats) or the revenue it holds that is attached to no system.
 *
 * ── ONE FUNCTION FOR BOTH TERMS, LIKE THE IMPACT SIDE ───────────────────────
 *
 * The two models fail in exactly the same four ways against exactly the same
 * dimension, so they share this function and four codes, and are told apart by
 * `subject.term`. Two near-identical functions is how the cost half and the
 * revenue half start disagreeing about which basis is admissible.
 *
 * ── THE COST MODEL STILL FIXES THE BASIS FIRST ──────────────────────────────
 *
 * `requiredBasis` is the window every money term of this role must agree on, or
 * `null` while none has been fixed. The caller resolves the cost model before
 * the revenue model and both before the loop, so the role's own cost remains the
 * basis-setter it was; the revenue model sets it only when no cost model was
 * authored. This function never mutates it — the caller adopts `periodBasis`
 * when it is non-null, which keeps "who decides the basis" in one place.
 *
 * ── WHY THERE IS NO POSITIVITY CHECK HERE ───────────────────────────────────
 *
 * The hours workload refuses zero and refuses negatives, because it is a
 * DENOMINATOR: a share of nothing is not a share, and a negative one divides
 * perfectly well into a confident negative percentage. The money terms are
 * SUMMED and reported, never divided here, so a zero is a legitimate answer (a
 * role that costs nothing to run, a role that holds no revenue) and a check
 * refusing it would report a broken model where there is only an unusual one.
 *
 * ⚠️ **ONE CONSUMER DOES DIVIDE A MONEY TERM, AND IT CARRIES THE SIGN CHECK
 * ITSELF.** `coverage-savings-projection.ts` divides `workloadCost` by the
 * worked hours to get a blended rate, where a negative cost would yield a
 * negative rate and an unflagged negative "saving" on every row. It refuses a
 * negative amount at ITS end rather than here, and that placement is the point:
 * a term is only wrong relative to what someone does with it, so the check
 * belongs with the division and not with the evaluation. Anything added here
 * would also refuse the amount for every consumer that merely reports it.
 */
export function evaluateWorkloadMoneyTerm(
  termName: CoverageMoneyTermName,
  formula: ValidatedCoverageFormula | null,
  context: ValidatedCoverageContext,
  requiredBasis: number | null
): CoverageWorkloadMoneyOutcome {
  if (formula === null) {
    return ABSENT_WORKLOAD_MONEY;
  }

  const subject = { kind: "workloadMoney", term: termName } as const;
  const named = WORKLOAD_MONEY_SUBJECT_TEXT[termName];
  const evaluated = evaluateCoverageFormula(formula, context);

  if (!evaluated.ok) {
    return {
      amount: null,
      warnings: [
        {
          code: "WORKLOAD_MONEY_MODEL_INVALID",
          severity: "error",
          subject,
          message: `${named} did not evaluate (${evaluated.error.code}): ${evaluated.error.message}`
        }
      ],
      consumedInputKeys: [],
      measuredInputKeys: [],
      periodBasis: null
    };
  }

  const { quantity, consumedInputKeys, measuredInputKeys } = evaluated.value;

  const rejected = (warning: CoverageWarning): CoverageWorkloadMoneyOutcome => ({
    amount: null,
    warnings: [warning],
    consumedInputKeys,
    measuredInputKeys,
    periodBasis: null
  });

  if (!isMoney(quantity)) {
    return rejected({
      code: "WORKLOAD_MONEY_WRONG_DIMENSION",
      severity: "error",
      subject,
      message:
        `${named} evaluates to ${formatCoverageDimension(quantity.dimension)}, ` +
        `not ${formatCoverageDimension(COVERAGE_MONEY_DIMENSION)} — it cannot be a ${termName}`
    });
  }

  if (!isAdmissibleCoverageRatioPeriodBasis(quantity.periodBasis)) {
    return rejected({
      code: "WORKLOAD_MONEY_INADMISSIBLE_PERIOD_BASIS",
      severity: "error",
      subject,
      message:
        `${named} is ${formatCoveragePeriodBasis(quantity.periodBasis)} — a ${termName} ` +
        "must be an absolute amount or an amount per year, so it is not reported"
    });
  }

  // Reachable only once the role carries two money models: the first one
  // through fixes the basis and has nothing to disagree with. The disagreement
  // is an `ABSOLUTE` term beside an `ANNUAL` one — a model that declares no
  // period beside one that does — because binding annualises every declared
  // period, so month-vs-year is NOT it. Both sides are exactly `MONEY`, so
  // nothing above can see this.
  if (coverageMoneyBasisDisagrees(quantity.periodBasis, requiredBasis)) {
    return rejected({
      code: "WORKLOAD_MONEY_WRONG_PERIOD_BASIS",
      severity: "error",
      subject,
      message:
        `${named} is ${formatCoveragePeriodBasis(quantity.periodBasis)} but this role's money ` +
        `figures are ${formatCoveragePeriodBasis(requiredBasis)} — same dimension, different ` +
        "window, so it cannot be counted"
    });
  }

  return {
    amount: quantity.magnitude,
    warnings: [],
    consumedInputKeys,
    measuredInputKeys,
    periodBasis: quantity.periodBasis
  };
}

/** True when a quantity is money and nothing but money. */
function isMoney(quantity: CoverageQuantity): boolean {
  return coverageDimensionsEqual(quantity.dimension, COVERAGE_MONEY_DIMENSION);
}
